import {Injectable} from '@angular/core';
import {keyToType, StockItem} from '../../../shared-utilities/models-old/datastructures';
import {PropertyToTitle} from '../../shared-utils/conversions/properties';

@Injectable({providedIn: 'root'})
export class ExportService {
  /**
   * Converts an array of objects to CSV and downloads it.
   *
   * @param data   The full data set (e.g. StockItem[]).
   * @param keys   Ordered list of keys to include in the CSV header & rows.
   * @param file   Optional filename (defaults to "export.csv").
   */
  exportStockItemDataAsCSV<T extends StockItem>(
    data: T[],
    keys: string[],
    file: string = 'export.csv',
  ): void {
    if (!data?.length || !keys?.length) {
      return;
    }

    const csv = this.#toCsv(data, keys);
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8'});
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = file;
    link.click();

    URL.revokeObjectURL(url);
  }

  #toCsv<T extends StockItem>(
    rows: T[],
    keys: string[],
  ): string {
    const header = keys.map((key: string) => PropertyToTitle[key]).join(',');

    const body = rows.map((r: T) =>
      keys.map((k: string) => this.#escape(r[k], k)).join(','),
    );

    return [header, ...body].join('\r\n');
  }

  #escape(value: unknown, key: string): string {
    if (value == null) {
      return '""';
    }

    let formattedValue: string;
    const valueType = keyToType[key as keyof StockItem];

    if (valueType === 'date' && value) {
      const date = new Date(value as string | number | Date);
      if (!isNaN(date.getTime())) {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        formattedValue = `${day}/${month}/${year}`;
      } else {
        formattedValue = String(value);
      }
    } else if (valueType === 'number' && typeof value === 'number') {
      formattedValue = value.toString();
    } else {
      formattedValue = String(value);
    }

    return `"${formattedValue.replace(/"/g, '""')}"`;
  }
}
